import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  Avatar,
  CardInfoContainer,
  Content,
  InfiniteScrollMarker,
  Label,
  ListItem,
  LoaderContainer,
  LoaderMoreContainer,
  PageContainer,
  Row,
  Text,
} from './CollectionUsersPage.styles';
import { Header, Spinner } from '../../../components';
import { timeAgo } from '../../../utils/date';
import { formatCurrency, getInitials } from '../../../utils/util';
import { Text16Bold } from '../../../components/utils/Text/Text.styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectFetchCollectionUsersInfo } from '../../../store/slices/collections/slice';
import { fetchCollectionUsers } from '../../../store/slices/collections/asyncThunks';
import { ReactComponent as PinIcon } from '../../../assets/icons/pin.svg';
import { CHAT, MESSAGES } from '../../../components/App/routes';

const CollectionUsersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loadMoreRef = useRef();
  const { collectionId, influencerId } = useParams();
  const [setShowContent] = useOutletContext();
  const location = useLocation();
  let { collectionName } = location.state;

  const [page, setPage] = useState(1);
  const { data, maxReached, pending, fetchMorePending } = useSelector(selectFetchCollectionUsersInfo);

  useEffect(() => {
    setPage(1);
    dispatch(fetchCollectionUsers({ collectionId, page: 1, search: '', ...(influencerId && { influencerId }) }));
  }, [dispatch, collectionId, influencerId]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !pending && !fetchMorePending && !maxReached) {
        setPage(prev => prev + 1);
        dispatch(
          fetchCollectionUsers({ collectionId, page: page + 1, search: '', ...(influencerId && { influencerId }) }),
        );
      }
    },
    [pending, maxReached, fetchMorePending, page, dispatch, collectionId, influencerId],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, { root: null, threshold: 1.0 });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleIntersection]);

  return (
    <PageContainer>
      <Header title={collectionName} setShowContent={setShowContent} />
      {pending ? (
        <LoaderContainer>
          <Spinner width={120} height={120} />
        </LoaderContainer>
      ) : (
        <Content>
          {data.map(item => {
            return (
              <ListItem
                key={item._id}
                onClick={() => {
                  navigate(
                    `${influencerId ? `/${influencerId}` : ''}/${MESSAGES}/${CHAT.replace(':chatId', item._id)}`,
                  );
                }}>
                <Row>
                  <CardInfoContainer>
                    <Avatar>{getInitials(item.displayName)}</Avatar>
                    <Text16Bold>{item.displayName}</Text16Bold>
                    {item.isPinned && <PinIcon width={16} height={16} />}
                  </CardInfoContainer>
                </Row>
                <Row>
                  <Label>Lifetime Spend</Label>
                  <Text>{formatCurrency(item.balance.totalSpent)}</Text>
                </Row>
                <Row>
                  <Label>Chat Started</Label>
                  <Text>{timeAgo(item.createdAt)}</Text>
                </Row>
                {item.lastPurchaseInfo.timestamp && (
                  <Row>
                    <Label>Last Spent Date</Label>
                    <Text>{timeAgo(item.lastPurchaseInfo.timestamp)}</Text>
                  </Row>
                )}
                <Row>
                  <Label>Last Responded Date</Label>
                  <Text>{timeAgo(item.lastMessage.timestamp)}</Text>
                </Row>
              </ListItem>
            );
          })}
          {fetchMorePending && (
            <LoaderMoreContainer>
              <Spinner width={100} height={100} />
            </LoaderMoreContainer>
          )}
          <InfiniteScrollMarker ref={loadMoreRef} />
        </Content>
      )}
    </PageContainer>
  );
};

export default CollectionUsersPage;
